"use client";

import React, { useState } from 'react';
import Image, { ImageProps } from 'next/image';
import { prepareGoogleFaviconDomain } from '@/lib/icons';

interface BrandLogoProps extends Omit<ImageProps, 'src' | 'onError'> {
  domain?: string;
  fallbackSrc?: string;
  logoUrl?: string;
}

/**
 * BrandLogo component with multiple fallback mechanisms.
 * Fallback chain: Contentful logo → Google Favicon → text initials.
 * Google Favicon is used as primary external source because it reliably
 * returns an icon for virtually every domain.
 */
export default function BrandLogo({
  domain,
  logoUrl,
  fallbackSrc,
  alt,
  width = 32,
  height = 32,
  ...props
}: BrandLogoProps) {
  const cleanDomain = domain ? prepareGoogleFaviconDomain(domain) : '';

  // Build ordered fallback chain
  const sources: string[] = [];
  if (logoUrl) sources.push(logoUrl);
  if (cleanDomain) {
    sources.push(`https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${cleanDomain}&size=128`);
  }
  if (fallbackSrc) sources.push(fallbackSrc);

  const [srcIndex, setSrcIndex] = useState(0);

  // If we've exhausted all sources, show text initials
  if (sources.length === 0 || srcIndex >= sources.length) {
    const initials = (typeof alt === 'string' ? alt : '').replace(/\s*logo$/i, '').substring(0, 2).toUpperCase() || 'BR';
    return (
      <div
        style={{
          width: Number(width),
          height: Number(height),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1a1a2e',
          color: '#e94560',
          borderRadius: '8px',
          fontSize: Math.max(Number(width) * 0.3, 12),
          fontWeight: 700,
        }}
      >
        {initials}
      </div>
    );
  }

  return (
    <Image
      src={sources[srcIndex]}
      alt={alt || 'Brand logo'}
      width={width}
      height={height}
      onError={() => setSrcIndex(i => i + 1)}
      {...props}
    />
  );
}